import { useEffect, useState } from 'react'
import { RankmiTable } from '@rankmi/rankmi-table'
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser'
import { faUserPlus } from '@fortawesome/pro-solid-svg-icons/faUserPlus'
import { useTranslation } from 'react-i18next'
import { useParams, useLocation } from 'react-router'
import { useHistory } from 'react-router-dom'
import { usersService } from 'api/configuration/users'
import { IUser, ISimpleUser } from 'interfaces/users'
import { AvatarWithInfo } from 'components/reusables/avatarWithInfo/AvatarWithInfo'
import { AccessLevelEnum } from 'enum/accessLevel'
import { PathRoutes } from 'enum/routes'
import { useRolesAndPermissions } from '../context/RolesAndPermissionsContext'
import { RankmiTableWrapper } from './styled'
import AccessLevel from './accessLevel'
import Options from './options'
import Remove from './remove'
import AssignRole from './assignRole'
import ChangeRole from './changeRole'

const Users = () => {
  const { t } = useTranslation()
  const { fetchRoleOptions } = useRolesAndPermissions()
  const { roleId } = useParams<{ roleId?: string }>()
  const location = useLocation()
  const history = useHistory()
  const prefix = 'configuration.rolesAndPermissions.users'
  const [users, setUsers] = useState<IUser[]>([])
  const [selectedUsers, setSelectedUsers] = useState<IUser[]>([])
  const [loading, setLoading] = useState(false)
  const [searchCriteria, setSearchCriteria] = useState('')
  const [order, setOrder] = useState('asc')
  const [fieldToOrder, setFieldToOrder] = useState('name')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [initialUsers, setInitialUsers] = useState<ISimpleUser[]>([])
  const [initialRole, setInitialRole] = useState(0)
  const [initialAccessLevel, setInitialAccessLevel] = useState(
    AccessLevelEnum.AllCollaborators
  )
  const [initialProfiles, setInitialProfiles] = useState<number[]>([])
  const [showAssignRoleModal, setShowAssignRoleModal] = useState(false)
  const [showRemoveAllRolesModal, setShowRemoveAllRolesModal] = useState(false)
  const [showBulkAssignmentModal, setShowBulkAssignmentModal] = useState(false)

  useEffect(() => {
    if (!location.pathname.includes('/users')) return
    getUsers(currentPage, searchCriteria, order, fieldToOrder, rowsPerPage)
  }, [location.pathname])

  useEffect(() => {
    if (roleId) {
      setInitialUsers([])
      setInitialRole(parseInt(roleId))
      setInitialAccessLevel(AccessLevelEnum.AllCollaborators)
      setInitialProfiles([])
      setShowAssignRoleModal(true)
    }
  }, [roleId])

  const getUsers = async (
    page: number,
    criteria: string,
    order: string,
    fieldToOrder: string,
    rowsPerPage: number
  ) => {
    const orderBy = order === 'descend' ? 'desc' : 'asc'
    setLoading(true)
    await usersService
      .getAssignedUsers(page, criteria, orderBy, fieldToOrder, rowsPerPage)
      .then((response) => {
        setUsers(response.data.users.map((user) => ({ ...user, key: user.id })))
        setTotalRecords(response.data.total)
      })
      .catch(() => {
        setUsers([])
        setTotalRecords(0)
      })
    setLoading(false)
  }

  const onSearch = (value: string) => {
    setSearchCriteria(value)
    setCurrentPage(1)
    getUsers(1, value, order, fieldToOrder, rowsPerPage)
  }

  const onPaginationChange = (value: number) => {
    setCurrentPage(value)
    getUsers(value, searchCriteria, order, fieldToOrder, rowsPerPage)
  }

  const onSortChange = (value: { order: string; field: string }) => {
    setOrder(value.order)
    setFieldToOrder(value.field)
    getUsers(currentPage, searchCriteria, value.order, value.field, rowsPerPage)
  }

  const onRowsPerPageChange = (value: number) => {
    setCurrentPage(1)
    setRowsPerPage(value)
    getUsers(1, searchCriteria, order, fieldToOrder, value)
  }

  const refreshView = async (options?: { resetSearch?: boolean }) => {
    if (roleId) {
      history.push(
        PathRoutes.ROLES_AND_PERMISSIONS_USERS.replace('/:roleId?', '')
      )
    }
    const criteria = options?.resetSearch ? '' : searchCriteria
    if (options?.resetSearch) {
      setSearchCriteria('')
    }
    setCurrentPage(1)
    await getUsers(1, criteria, order, fieldToOrder, rowsPerPage)
    await fetchRoleOptions()
  }

  const bulkAssignment = () => {
    setInitialUsers(selectedUsers)
    setInitialRole(0)
    setInitialAccessLevel(AccessLevelEnum.AllCollaborators)
    setInitialProfiles([])
    setShowAssignRoleModal(true)
    setShowBulkAssignmentModal(false)
  }

  const assignRole = () => {
    setInitialUsers([])
    setInitialRole(0)
    setInitialAccessLevel(AccessLevelEnum.AllCollaborators)
    setInitialProfiles([])
    setShowAssignRoleModal(true)
  }

  const changeRole = (user: IUser) => {
    setInitialUsers([user])
    setInitialRole(user.role.id)
    setInitialAccessLevel(user.accessLevel)
    setInitialProfiles(user.profiles)
    setShowAssignRoleModal(true)
  }

  const closeAssignRoleModal = () => {
    setShowAssignRoleModal(false)
    if (roleId) {
      history.push(
        PathRoutes.ROLES_AND_PERMISSIONS_USERS.replace('/:roleId?', '')
      )
    }
  }

  const onRowSelected = (selectedRowKeys: number[], newRows: IUser[]) => {
    setSelectedRows(selectedRowKeys)
    setSelectedUsers((prev) => {
      const combined = [...prev, ...newRows]
      const uniqueFiltered = Array.from(
        new Map(
          combined
            .filter((user) => selectedRowKeys.includes(user.id))
            .map((user) => [user.id, user])
        ).values()
      )
      return uniqueFiltered
    })
  }

  const locale = {
    header: {
      addButton: t(`${prefix}.assignRole`),
      rowsCount: t(`${prefix}.table.rowsCountV2`),
      rowsCount_plural: t(`${prefix}.table.rowsCount_pluralV2`),
    },
  }

  const columns = [
    {
      title: `${t(`${prefix}.table.headers.name`)}`,
      key: 'name',
      sorteable: true,
      width: 179,
      fixed: 'left',
      hideable: false,
      render: (_name: string, user: IUser) => (
        <AvatarWithInfo
          borderRadius={50}
          icon={faUser}
          image={user.avatar}
          principalColor="#0085FF"
          background="#0085FF"
          size={30}
          title={`${user.firstName} ${user.lastName}`}
          subtitle={user.identifier}
          fontWeight="normal"
        />
      ),
    },
    {
      title: `${t(`${prefix}.table.headers.role`)}`,
      key: 'abac_role_name',
      sorteable: true,
      width: 165,
      render: (_abacRoleName: string, user: IUser) => (
        <AvatarWithInfo
          principalColor="initial"
          title={user.role.name}
          subtitle={user.role.code}
          fontWeight="normal"
          size={30}
          borderRadius={8}
        />
      ),
    },
    {
      title: `${t(`${prefix}.table.headers.accessLevel.title`)}`,
      key: 'access_level',
      sorteable: true,
      width: 188,
      render: (_accessLevel: number, user: IUser) => (
        <AccessLevel user={user} />
      ),
    },
    {
      title: `${t(`${prefix}.table.headers.position`)}`,
      key: 'position_model_name',
      sorteable: true,
      width: 202,
      render: (_positionModelName: string, user: IUser) => (
        <AvatarWithInfo
          principalColor="initial"
          title={user.position.name}
          subtitle={user.position.code}
          fontWeight="normal"
          size={30}
          borderRadius={8}
        />
      ),
    },
    {
      title: `${t(`${prefix}.table.headers.area`)}`,
      key: 'area_name',
      sorteable: true,
      width: 195,
      render: (_areaName: string, user: IUser) => (
        <AvatarWithInfo
          principalColor="initial"
          title={user.area.name}
          subtitle={user.area.code}
          fontWeight="normal"
          size={30}
          borderRadius={8}
        />
      ),
    },
    {
      width: 160,
      align: 'center',
      fixed: 'right',
      hideable: false,
      render: (user: IUser) => (
        <Options
          user={user}
          refreshView={refreshView}
          changeRole={changeRole}
        />
      ),
    },
  ]

  const actions = [
    {
      label: t(`${prefix}.actions.changeRole.title`).replace(
        '{count}',
        selectedRows.length.toString()
      ),
      onClick: () => setShowBulkAssignmentModal(true),
      disabled: selectedRows.length < 2,
    },
    {
      label: t(`${prefix}.actions.removeAllRoles.title`).replace(
        '{count}',
        selectedRows.length.toString()
      ),
      onClick: () => setShowRemoveAllRolesModal(true),
      disabled: selectedRows.length < 2,
    },
  ]

  return (
    <RankmiTableWrapper>
      <RankmiTable
        columnHeaders={columns}
        addButtonIcon={faUserPlus}
        onAddClick={assignRole}
        data={users}
        countIcon={faUser}
        search={searchCriteria}
        onSearch={onSearch}
        total={totalRecords}
        locale={locale}
        loading={loading}
        onSortChange={onSortChange}
        onPaginationChange={onPaginationChange}
        currentPage={currentPage}
        initialRowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        actions={actions}
        selectedRows={selectedRows}
        onRowSelected={({ selectedRowKeys, selectedRows }) =>
          onRowSelected(selectedRowKeys, selectedRows)
        }
        showScrollButtons
        simpleSelectAll
        changeColumnsVisibility
        delaySearch
        responsive
      />
      {showRemoveAllRolesModal && (
        <Remove
          users={selectedRows}
          refreshView={refreshView}
          close={() => setShowRemoveAllRolesModal(false)}
        />
      )}
      {showAssignRoleModal && (
        <AssignRole
          initialUsers={initialUsers}
          initialRole={initialRole}
          initialAccessLevel={initialAccessLevel}
          initialProfiles={initialProfiles}
          isRolePredetermined={!!roleId}
          refreshView={refreshView}
          close={closeAssignRoleModal}
        />
      )}
      {showBulkAssignmentModal && (
        <ChangeRole
          users={selectedRows.length}
          assignRole={bulkAssignment}
          close={() => setShowBulkAssignmentModal(false)}
        />
      )}
    </RankmiTableWrapper>
  )
}

export default Users
